import chatConstants from 'constants/chats';
import { DESKTOP_MIN_WIDTH } from 'constants/resolutions';
import { Task } from 'redux-saga';
import { call, cancel, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { SearchDialogUrlParams, getSearchDialogUrl } from 'redux/utils/searchInDialog.utils';
import { uploadFile } from 'redux/utils/uploadUtils';
import safeFetch from 'shared/api/safeFetch';
import {
  ChatDescription,
  ChatInterlocutor,
  ChatMessage,
  DialogType,
  Folder,
  SearchChatUser,
  SelectedDialogInfo,
  messageStatusEnum,
} from 'types/chatsType';
import { FileDescriptor } from 'types/fileTypes';
import {
  CreatePollPayload,
  GetPollAnswerUsersPayload,
  POLL_ANSWER_USERS_PAGE_SIZE,
  PollCreateResponse,
  PollResultsDetails,
  PollResultsVoter,
  PollStopStatsResponse,
  PollVoteStatsResponse,
  SubmitPollAnswerPayload,
} from 'types/pollTypes';
import { ActionType, PayloadErrorType } from 'types/reduxTypes';
import { ChatType, RootState } from 'types/stateTypes';
import { mergePollVoteStats, pollApiPaths } from 'utils/poll.utils';

import {
  AdminRolePayload,
  CreateChatFolderPayload,
  CreateGroupPayload,
  CreateGroupSuccessPayload,
  DeleteMessagesPayload,
  EditChatFolderPayload,
  FetchChatsPayload,
  FetchChatsSuccessPayload,
  FetchLastChatPayload,
  GetChatInfoPayload,
  GetUpdatedChatInfoPayload,
  GetUsersListPayload,
  InviteUserPayload,
  NavigateToChatMessagePayload,
  PinChatPayload,
  ReadMessagesPayload,
  SearchChatsPayload,
  UpdateGroupPayload,
  UpdateGroupSuccessPayload,
  UpdateMessagePayload,
  UploadAttachedFilePayload,
  UploadGroupAvatarPayload,
  UsersStatusSuccessItem,
  blockDialogFailed,
  blockDialogSuccess,
  changeLoadMessageStatus,
  clearDialogInfo,
  createChatFolderFailed,
  createChatFolderSuccess,
  createGroupFailed,
  createGroupSuccess,
  createPollFailed,
  createPollSuccess,
  deleteChatFailed,
  deleteChatFolderFailed,
  deleteChatFolderSuccess,
  deleteChatSuccess,
  deleteGroupChatFailed,
  deleteGroupChatSuccess,
  deleteMessagesFailed,
  deleteMessagesSuccess,
  disableNotificationsFailed,
  disableNotificationsSuccess,
  editChatFolderFailed,
  editChatFolderSuccess,
  enableNotificationsFailed,
  enableNotificationsSuccess,
  fetchChatsFailed,
  fetchChatsSuccess,
  getChatFoldersFailed,
  getChatFoldersSuccess,
  getChatInfo as getChatInfoAction,
  getChatInfoFailed,
  getChatInfoSuccess,
  getDialogInfo as getDialogInfoAction,
  getDialogInfoByPeer,
  getDialogInfoByPeerFailed,
  getDialogInfoByPeerSuccess,
  getDialogInfoFailed,
  getDialogInfoSuccess,
  getFolderDialogsFailed,
  getFolderDialogsSuccess,
  getPollAnswerUsersFailed,
  getPollAnswerUsersSuccess,
  getPollFailed,
  getPollSuccess,
  getUpdatedChatInfoFailed,
  getUpdatedChatInfoSuccess,
  getUsersListFailed,
  getUsersListSuccess,
  getUsersStatusFailed,
  getUsersStatusSuccess,
  inviteUserFailed,
  inviteUserSuccess,
  leaveGroupFailed,
  leaveGroupSuccess,
  makeUserAdminFailed,
  makeUserAdminSuccess,
  openExistingDialog,
  openPendingChat,
  pinChatFailed,
  pinChatSuccess,
  readMessages,
  readMessagesFailed,
  readMessagesSuccess,
  removeAdminFailed,
  removeAdminSuccess,
  reorderFoldersFailed,
  reorderFoldersSuccess,
  searchDialogsFailed,
  searchDialogsSuccess,
  searchGlobalChatFailed,
  searchGlobalChatSuccess,
  searchInDialogFailed,
  searchInDialogSuccess,
  selectChat,
  selectChatFolder,
  stopPollFailed,
  stopPollSuccess,
  submitPollAnswerFailed,
  submitPollAnswerSuccess,
  unblockDialogFailed,
  unblockDialogSuccess,
  unpinChatFailed,
  unpinChatSuccess,
  updateGroupFailed,
  updateGroupSuccess,
  updateMessageFailed,
  updateMessageSuccess,
  uploadAttachedFileSuccess,
  uploadFileFailed,
  uploadGroupAvatarSuccess,
} from '../actions/chatsActions';
import {
  BLOCK_DIALOG,
  CREATE_GROUP,
  CREATE_POLL,
  DELETE_CHAT,
  DELETE_GROUP_CHAT,
  DELETE_MESSAGES,
  DISABLE_NOTIFICATIONS,
  ENABLE_NOTIFICATIONS,
  FETCH_CHATS,
  FETCH_LAST_CHAT,
  GET_CHAT_INFO,
  GET_DIALOG_INFO,
  GET_POLL,
  GET_POLL_ANSWER_USERS,
  GET_UPDATED_CHAT_INFO,
  GET_USERS_LIST,
  GET_USERS_STATUS,
  INVITE_USER,
  LEAVE_GROUP,
  MAKE_USER_ADMIN,
  NAVIGATE_TO_CHAT_MESSAGE_REQUEST,
  OPEN_DIALOG_WITH_USER,
  PIN_CHAT,
  READ_MESSAGES,
  REMOVE_ADMIN,
  SEARCH_DIALOGS,
  SEARCH_GLOBAL_CHAT,
  SEARCH_IN_DIALOG,
  STOP_POLL,
  SUBMIT_POLL_ANSWER,
  UNBLOCK_DIALOG,
  UNPIN_CHAT,
  UPDATE_GROUP,
  UPDATE_MESSAGE,
  UPLOAD_ATTACHED_FILE,
  UPLOAD_GROUP_AVATAR,
} from '../constants/chats';

import {
  CREATE_CHAT_FOLDER,
  DELETE_CHAT_FOLDER,
  EDIT_CHAT_FOLDER,
  GET_CHAT_FOLDERS,
  GET_FOLDER_DIALOGS,
  REORDER_FOLDERS,
} from '../constants/chatFolders';

import { findDialogByPeerId } from '../model/chats/pendingChat';
import { filterUnreadMessages } from '../model/chats/unread';
import { generatePathToGetMessages, getMessages, parseFolderDialogs, parseFoundChats } from '../utils/chatUtils';

const BASE_CHAT_URL = '/api/chat/v1/dialogs';

type SafeFetchResult<T> = { data: T };

type SagaError = Error & {
  data?: PayloadErrorType & { message?: string };
};

type DialogInfoByPeerResponse = Partial<SelectedDialogInfo> & {
  id?: number;
  isDeleted?: boolean;
  interlocutor?: ChatInterlocutor;
};

const isOpenedDialogByPeer = (data: DialogInfoByPeerResponse): data is SelectedDialogInfo =>
  typeof data.id === 'number' && !data.isDeleted && (data.type === DialogType.chat || data.type === DialogType.group);

type GetUsersListSuccessData = { users: SearchChatUser[] };

const errorMessage = (error: unknown): string => (error as SagaError).message;

const getDialogPinQuery = (folderId?: number | null): string => {
  if (folderId == null || folderId === chatConstants.folders.all) {
    return '';
  }
  return `?folderId=${folderId}`;
};

function* getUsersList(action: ActionType<GetUsersListPayload>) {
  try {
    const userId: string | null = yield select((state: RootState) => state.auth.id);

    const { data } = (yield call(
      safeFetch,
      `/api/chat/v1/users/search?searchField=${action.payload.searchTextValue}`,
      {
        headers: { access: '*/*' },
        method: 'GET',
      }
    )) as SafeFetchResult<GetUsersListSuccessData>;
    yield put(
      getUsersListSuccess({
        data,
        groupMembersIds: action.payload.groupMembersIds,
        isCreateGroup: action.payload.isCreateGroup,
        isGroup: action.payload.isGroup,
        userId: userId ?? undefined,
      })
    );
  } catch (error) {
    yield put(getUsersListFailed(errorMessage(error)));
  }
}

function* fetchLastChat(action: ActionType<FetchLastChatPayload>) {
  const {
    payload: { lastChatId, unreadMessages },
  } = action;
  yield put(selectChat(lastChatId));
  yield put(getChatInfoAction({ loadMore: false, selectedChatId: lastChatId, unreadMessages }));
  yield put(getDialogInfoAction(lastChatId));
}

function* fetchChats({ payload }: ActionType<FetchChatsPayload | undefined>) {
  try {
    const lastMessageId = payload?.lastMessageId;
    const folderId = payload?.folderId;
    const additionalFilters = payload?.additionalFilters;

    const requestParams = [];
    if (folderId) requestParams.push(`folderId=${folderId}`);
    if (lastMessageId) requestParams.push(`lastMessageId=${lastMessageId}`);
    if (additionalFilters) requestParams.push(`view=${additionalFilters}`);

    const { data } = (yield call(
      safeFetch,
      `${BASE_CHAT_URL}${requestParams ? '?' : ''}${requestParams.join('&')}`,
      {
        headers: { access: '*/*' },
        method: 'GET',
      }
    )) as SafeFetchResult<FetchChatsSuccessPayload>;

    if (payload?.isAppend) data.isAppend = true;

    yield put(fetchChatsSuccess(data));
  } catch (error) {
    yield put(fetchChatsFailed(errorMessage(error)));
  }
}

function* getChatInfo(action: ActionType<GetChatInfoPayload>) {
  const { loadMore, selectedChatId, unreadMessages } = action.payload;
  const userId: string | null = yield select((state: RootState) => state.auth.id);
  const { chatInfo, selectedDialogInfo, selectedChatId: stateSelectedChatId }: ChatType = yield select(
    (state: RootState) => state.chats
  );
  const lastMessageId = chatInfo[0]?.id;
  const isAllMessagesLoaded = Boolean(loadMore && lastMessageId === selectedDialogInfo?.firstMessageId);
  try {
    if (isAllMessagesLoaded) {
      yield put(changeLoadMessageStatus(false));
      return;
    }
    const path = generatePathToGetMessages({ chatInfo, loadMore, selectedChatId, stateSelectedChatId });
    const { data } = (yield call(getMessages, path)) as SafeFetchResult<ChatMessage[]>;
    yield put(getChatInfoSuccess({ data, id: selectedChatId, loadMore }));
    if (unreadMessages !== 0 && userId) {
      const messages = filterUnreadMessages({ messages: data, myId: userId });
      if (messages.length) {
        yield put(readMessages({ id: selectedChatId, messages, status: messageStatusEnum.read }));
      }
    }
  } catch (error) {
    yield put(getChatInfoFailed({ id: selectedChatId, loadMore, message: errorMessage(error) }));
  }
}

function* pinChat(action: ActionType<PinChatPayload>) {
  const { dialogId, folderId } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/pin/${dialogId}${getDialogPinQuery(folderId)}`, {
      body: {},
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(pinChatSuccess(dialogId));
  } catch (error) {
    yield put(pinChatFailed(errorMessage(error)));
  }
}

function* unpinChat(action: ActionType<PinChatPayload>) {
  const { dialogId, folderId } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/unpin/${dialogId}${getDialogPinQuery(folderId)}`, {
      body: {},
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(unpinChatSuccess(dialogId));
  } catch (error) {
    yield put(unpinChatFailed(errorMessage(error)));
  }
}

function* enableNotifications(action: ActionType<number>) {
  const dialogId = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/enable-notifications/${dialogId}`, {
      body: {},
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(enableNotificationsSuccess(dialogId));
  } catch (error) {
    yield put(enableNotificationsFailed(errorMessage(error)));
  }
}

function* disableNotifications(action: ActionType<number>) {
  const dialogId = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/disable-notifications/${dialogId}`, {
      body: {},
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(disableNotificationsSuccess(dialogId));
  } catch (error) {
    yield put(disableNotificationsFailed(errorMessage(error)));
  }
}

function* openDialogWithUser(action: ActionType<string>) {
  const userId = action.payload;
  if (!userId) {
    return;
  }

  const existingChat: ChatDescription | undefined = yield select((state: RootState) =>
    findDialogByPeerId(state.chats.chats, userId)
  );
  if (existingChat) {
    yield put(openExistingDialog(existingChat.id));
    yield put(getDialogInfoAction(existingChat.id));
    return;
  }

  yield put(getDialogInfoByPeer());

  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/info/by-peer/${userId}?details=2`, {
      headers: { access: '*/*' },
      method: 'GET',
    })) as SafeFetchResult<DialogInfoByPeerResponse>;

    if (isOpenedDialogByPeer(data)) {
      yield put(getDialogInfoByPeerSuccess(data));
      return;
    }

    if (data.interlocutor) {
      yield put(openPendingChat(data.interlocutor));
      return;
    }

    yield put(getDialogInfoByPeerFailed('interlocutor is missing'));
  } catch (error) {
    yield put(getDialogInfoByPeerFailed(errorMessage(error)));
  }
}

function* createGroup(action: ActionType<CreateGroupPayload>) {
  try {
    yield put(selectChatFolder(chatConstants.folders.all));
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/create`, {
      body: {
        dialogImage: action.payload.image,
        dialogName: action.payload.name || null,
        users: action.payload.ids,
      },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    })) as SafeFetchResult<CreateGroupSuccessPayload>;
    yield put(createGroupSuccess(data));
    yield put(getDialogInfoAction(data.id));
  } catch (error) {
    yield put(createGroupFailed(errorMessage(error)));
  }
}

function* deleteChat(action: ActionType<number>) {
  const deviceWidth: number = yield select((state: RootState) => state.user.deviceWidth);
  const isMobile = deviceWidth < DESKTOP_MIN_WIDTH;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/${action.payload}`, {
      headers: { Accept: 'application/json', access: '*/*' },
      method: 'DELETE',
    });
    yield put(deleteChatSuccess({ id: action.payload, isMobile }));
  } catch (error) {
    yield put(deleteChatFailed(errorMessage(error)));
  }
}

function* deleteGroupChat(action: ActionType<number>) {
  try {
    const deviceWidth: number = yield select((state: RootState) => state.user.deviceWidth);
    const isMobile = deviceWidth < DESKTOP_MIN_WIDTH;
    yield call(safeFetch, `${BASE_CHAT_URL}/chat/${action.payload}`, {
      headers: { access: '*/*' },
      method: 'DELETE',
    });

    yield put(deleteGroupChatSuccess({ id: action.payload, isMobile }));
  } catch (error) {
    yield put(deleteGroupChatFailed(errorMessage(error)));
  }
}

function* updateMessage(action: ActionType<UpdateMessagePayload>) {
  try {
    yield call(safeFetch, `/api/chat/v1/messages/update`, {
      body: action.payload,
      headers: { 'Content-Type': 'application/json;charset=UTF-8', access: '*/*' },
      method: 'PATCH',
    });
    yield put(updateMessageSuccess(action.payload));
  } catch (error) {
    yield put(updateMessageFailed(errorMessage(error)));
  }
}

function* deleteMessages(action: ActionType<DeleteMessagesPayload>) {
  const { messages, forMe } = action.payload;
  try {
    yield call(safeFetch, `/api/chat/v1/messages?forMe=${forMe}`, {
      body: { messages },
      headers: { 'Content-Type': 'application/json;charset=UTF-8', access: '*/*' },
      method: 'DELETE',
    });
    yield put(deleteMessagesSuccess());
  } catch (error) {
    const errorText = (error as SagaError).data?.message;
    yield put(deleteMessagesFailed(errorText ?? ''));
  }
}

function* getDialogInfo(action: ActionType<number>) {
  const id = action.payload;
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/info/${id}?details=2`, {
      headers: { access: '*/*' },
      method: 'GET',
    })) as SafeFetchResult<SelectedDialogInfo>;
    yield put(getDialogInfoSuccess(data));
  } catch (error) {
    yield put(getDialogInfoFailed(errorMessage(error)));
  }
}

function* getUsersStatus(action: ActionType<string[]>) {
  try {
    const { data } = (yield call(safeFetch, `/api/chat/v1/users/status`, {
      body: { users: action.payload },
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    })) as SafeFetchResult<UsersStatusSuccessItem[]>;
    yield put(getUsersStatusSuccess(data));
  } catch (error) {
    yield put(getUsersStatusFailed(errorMessage(error)));
  }
}

function* readMessagesHandler(action: ActionType<ReadMessagesPayload>) {
  const selectedChatId: ChatType['selectedChatId'] = yield select((state: RootState) => state.chats.selectedChatId);
  try {
    if (selectedChatId === action.payload.id) {
      const body = {
        dialogId: action.payload.id,
        messages: action.payload.messages,
        readStatus: action.payload.status,
      };
      yield call(safeFetch, `/api/chat/v1/messages/read`, {
        body,
        headers: { 'Content-Type': 'application/json;charset=UTF-8' },
        method: 'PATCH',
      });
      yield put(readMessagesSuccess(body));
    }
  } catch (error) {
    yield put(readMessagesFailed(errorMessage(error)));
  }
}

function* inviteUser(action: ActionType<InviteUserPayload>) {
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/invite`, {
      body: {
        dialogId: action.payload.id,
        user: action.payload.user,
      },
      headers: { access: '*/*' },
      method: 'POST',
    });
    yield put(inviteUserSuccess(action.payload.id));
  } catch (error) {
    yield put(inviteUserFailed(errorMessage(error)));
  }
}

function* uploadAttachedFile(action: ActionType<UploadAttachedFilePayload>) {
  try {
    const { formData, fileInfo } = action.payload;
    const { data } = (yield call(uploadFile, formData)) as SafeFetchResult<FileDescriptor[]>;
    let mergedData: FileDescriptor[] = data;
    if (data && data.length && fileInfo && fileInfo.length === data.length) {
      mergedData = data.map((fileDescriptor, index) => ({
        ...fileDescriptor,
        fileType: fileInfo[index].fileType as FileDescriptor['fileType'],
      }));
    }
    yield put(uploadAttachedFileSuccess({ data: mergedData }));
  } catch (error) {
    yield put(uploadFileFailed(errorMessage(error)));
  }
}

function* uploadGroupAvatar(action: ActionType<UploadGroupAvatarPayload>) {
  try {
    const { formData } = action.payload;
    const { data } = (yield call(uploadFile, formData)) as SafeFetchResult<FileDescriptor | FileDescriptor[]>;
    yield put(uploadGroupAvatarSuccess({ data }));
  } catch (error) {
    yield put(uploadFileFailed(errorMessage(error)));
  }
}

function* updateGroupHandler(action: ActionType<UpdateGroupPayload>) {
  const { id: chatId, image, name, users } = action.payload;
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/${chatId}`, {
      body: { image, name, users },
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      method: 'PATCH',
    })) as SafeFetchResult<UpdateGroupSuccessPayload>;
    yield put(updateGroupSuccess(data));
  } catch (error) {
    yield put(updateGroupFailed((error as SagaError).data as PayloadErrorType));
  }
}

function* leaveGroup(action: ActionType<number>) {
  try {
    const deviceWidth: number = yield select((state: RootState) => state.user.deviceWidth);
    const isMobile = deviceWidth < DESKTOP_MIN_WIDTH;
    yield call(safeFetch, `${BASE_CHAT_URL}/leave/${action.payload}`, {
      headers: { access: '*/*' },
      method: 'PATCH',
    });
    yield put(leaveGroupSuccess({ id: action.payload, isMobile }));
  } catch (error) {
    yield put(leaveGroupFailed(errorMessage(error)));
  }
}

function* searchInDialog(action: ActionType<SearchDialogUrlParams>) {
  const { isAppend } = action.payload;

  try {
    const url = getSearchDialogUrl(BASE_CHAT_URL, action.payload);
    const response: Response = yield call(fetch, url, {
      credentials: 'include',
      headers: { access: '*/*' },
      method: 'GET',
    });

    if (response.status === 404) {
      yield put(searchInDialogSuccess({ isAppend, payload: [] }));
      return;
    }

    if (!response.ok) {
      throw new Error('error fetch');
    }

    const data: ChatMessage[] = yield call([response, 'json']);
    yield put(searchInDialogSuccess({ isAppend, payload: data }));
  } catch (error) {
    yield put(searchInDialogFailed(errorMessage(error)));
  }
}

function navigateToChatMessageRequestHandler(action: ActionType<NavigateToChatMessagePayload>) {
  const { dialogId, messageId } = action.payload;
  // eslint-disable-next-line no-console
  console.log('[navigateToChatMessage]', { dialogId, messageId });
}

function* getUpdatedChatInfo(action: ActionType<GetUpdatedChatInfoPayload>) {
  const dialogId = action.payload.id;
  const selectedChatId: ChatType['selectedChatId'] = yield select((state: RootState) => state.chats.selectedChatId);
  const chats: ChatType['chats'] = yield select((state: RootState) => state.chats.chats);
  const messageInfo = action.payload.messageInfo;
  const messageInfoType = messageInfo?.type;
  const fullMessage = action.payload.fullMessage;
  const isChatInList = chats.some((chat) => chat.id === dialogId);
  const shouldFetch =
    !isChatInList ||
    (messageInfoType != null &&
      (messageInfoType !== 'changeChat' ||
        (dialogId === selectedChatId && messageInfoType === 'changeChat' && messageInfo?.users)));

  if (shouldFetch) {
    try {
      const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/info/${dialogId}?details=2`, {
        headers: { access: '*/*' },
        method: 'GET',
      })) as SafeFetchResult<SelectedDialogInfo>;
      yield put(
        getUpdatedChatInfoSuccess({
          ...data,
          ...(fullMessage != null && { fullMessage }),
        })
      );
    } catch (error) {
      yield put(getUpdatedChatInfoFailed(errorMessage(error)));
    }
  }
}

/** несколько WS не генерируют параллельные запросы /info */
export function* getUpdatedChatInfoWatcher(): Generator {
  const tasksByDialogId: Record<number, Task> = {};

  yield takeEvery(GET_UPDATED_CHAT_INFO, function* (action: ActionType<GetUpdatedChatInfoPayload>) {
    const dialogId = action.payload.id;
    const prevTask = tasksByDialogId[dialogId];
    if (prevTask?.isRunning()) {
      yield cancel(prevTask);
    }
    let task: Task;
    // eslint-disable-next-line prefer-const
    task = (yield fork(function* () {
      try {
        yield call(getUpdatedChatInfo, action);
      } finally {
        if (tasksByDialogId[dialogId] === task) {
          delete tasksByDialogId[dialogId];
        }
      }
    })) as Task;
    tasksByDialogId[dialogId] = task;
  });
}

function* getFolderDialogsHandler(action: ActionType<{ id: Folder['id'] }>) {
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/folder/${action.payload.id}/dialogs`, {
      headers: { access: '*/*' },
      method: 'GET',
    })) as SafeFetchResult<unknown[]>;
    const parsedData = parseFolderDialogs(data);
    yield put(getFolderDialogsSuccess(parsedData || []));
  } catch (error) {
    yield put(getFolderDialogsFailed(errorMessage(error)));
  }
}

function* getChatFolders() {
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/folder`, {
      headers: { access: '*/*' },
      method: 'GET',
    })) as SafeFetchResult<{ folders?: Folder[] }>;
    yield put(getChatFoldersSuccess(data?.folders || []));
  } catch (error) {
    yield put(getChatFoldersFailed(errorMessage(error)));
  }
}

function* reorderFolders(action: ActionType<Folder[]>) {
  const folderIds = action.payload.map((folder) => folder.id);
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/folder/reorder`, {
      body: { folderIds },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'PATCH',
    });
    yield put(reorderFoldersSuccess());
  } catch (error) {
    yield put(reorderFoldersFailed(errorMessage(error)));
  }
}

function* createChatFolder(action: ActionType<CreateChatFolderPayload>) {
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}/folder`, {
      body: {
        dialogIds: action.payload.dialogIds,
        name: action.payload.name,
        // ord: action.payload.ord,
      },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    })) as SafeFetchResult<Folder>;
    const result = { ...data, unreadCount: 0 };
    yield put(createChatFolderSuccess(result));
    yield put(clearDialogInfo());
    yield put(selectChatFolder(result.id));
  } catch (error) {
    yield put(createChatFolderFailed(errorMessage(error)));
  }
}

function* editChatFolder(action: ActionType<EditChatFolderPayload>) {
  const { dialogs, dialogIds, id, name, ord } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/folder`, {
      body: { dialogIds, id, name, ord },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'PATCH',
    });
    yield put(editChatFolderSuccess({ dialogIds, dialogs, id, name, ord }));
  } catch (error) {
    yield put(editChatFolderFailed(errorMessage(error)));
  }
}

function* deleteChatFolder(action: ActionType<Folder['id']>) {
  const folderId = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/folder/${folderId}`, {
      headers: { Accept: 'application/json', access: '*/*' },
      method: 'DELETE',
    });
    yield put(clearDialogInfo());
    yield put(selectChatFolder(chatConstants.folders.all));
    yield put(deleteChatFolderSuccess(action.payload));
  } catch (error) {
    yield put(deleteChatFolderFailed(errorMessage(error)));
  }
}

const searchChats = async (payload?: Partial<SearchChatsPayload>): Promise<SafeFetchResult<unknown[]>> => {
  const category = payload?.category || '21';
  const query = payload?.query;
  const details = payload?.details;
  const requestParams = [];
  if (category) requestParams.push(`category=${category}`);
  if (details) requestParams.push(`details=${details}`);
  if (query) requestParams.push(`q=${query}`);
  const result = await safeFetch(`${BASE_CHAT_URL}/global/search${requestParams ? '?' : ''}${requestParams.join('&')}`, {
    headers: { access: '*/*' },
    method: 'GET',
  });
  return { data: result?.data ?? [] };
};

function* searchDialogsHandler(action: ActionType<SearchChatsPayload>) {
  try {
    const { data } = yield call(searchChats, action.payload);
    yield put(searchDialogsSuccess(parseFoundChats(data)));
  } catch (error) {
    yield put(searchDialogsFailed(errorMessage(error)));
  }
}

function* searchChatsHandler(action: ActionType<SearchChatsPayload>) {
  try {
    const { data } = yield call(searchChats, action.payload);
    yield put(searchGlobalChatSuccess(data as ChatType['foundChats']));
  } catch (error) {
    yield put(searchGlobalChatFailed(errorMessage(error)));
  }
}

function* makeAdmin(action: ActionType<AdminRolePayload>) {
  const { dialogId, userId } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/make-admin/${dialogId}`, {
      body: { user: userId },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(makeUserAdminSuccess(userId as string));
  } catch (error) {
    yield put(makeUserAdminFailed(errorMessage(error)));
  }
}

function* removeAdmin(action: ActionType<AdminRolePayload>) {
  const { dialogId, userId } = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/remove-admin/${dialogId}`, {
      body: { user: userId },
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'PATCH',
    });
    yield put(removeAdminSuccess(userId as string));
  } catch (error) {
    yield put(removeAdminFailed(errorMessage(error)));
  }
}

function* blockDialog(action: ActionType<number>) {
  const dialogId = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/block/${dialogId}`, {
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(clearDialogInfo());
    yield put(blockDialogSuccess(dialogId));
  } catch (error) {
    yield put(blockDialogFailed(errorMessage(error)));
  }
}

function* unblockDialog(action: ActionType<number>) {
  const dialogId = action.payload;
  try {
    yield call(safeFetch, `${BASE_CHAT_URL}/unblock/${dialogId}`, {
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    });
    yield put(clearDialogInfo());
    yield put(unblockDialogSuccess(dialogId));
  } catch (error) {
    yield put(unblockDialogFailed(errorMessage(error)));
  }
}

function* createPollHandler(action: ActionType<CreatePollPayload>) {
  const { payload } = action;
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}${pollApiPaths.create}`, {
      body: payload,
      headers: { 'Content-Type': 'application/json', access: '*/*' },
      method: 'POST',
    })) as SafeFetchResult<PollCreateResponse>;
    yield put(createPollSuccess(data));
  } catch (error) {
    yield put(createPollFailed(errorMessage(error)));
  }
}

function* submitPollAnswerHandler({ payload }: ActionType<SubmitPollAnswerPayload>) {
  const { pollId, optionIds } = payload;
  try {
    const response = (yield call(safeFetch, `${BASE_CHAT_URL}${pollApiPaths.answer(pollId)}`, {
      body: { optionIds },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        access: '*/*',
      },
      method: 'PUT',
    })) as SafeFetchResult<PollVoteStatsResponse>;

    const chatInfo: ChatType['chatInfo'] = yield select((state: RootState) => state.chats.chatInfo);
    const chatPoll = chatInfo.find((message) => message.poll?.i === pollId)?.poll;

    if (!chatPoll) {
      yield put(submitPollAnswerSuccess({ poll: null }));
      return;
    }

    const poll = mergePollVoteStats(chatPoll, response?.data, optionIds);
    yield put(submitPollAnswerSuccess({ poll }));
  } catch (error) {
    yield put(submitPollAnswerFailed());
  }
}

function* stopPollHandler({ payload }: ActionType<{ pollId: string }>) {
  const { pollId } = payload;
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}${pollApiPaths.stop(pollId)}`, {
      headers: { access: '*/*' },
      method: 'POST',
    })) as SafeFetchResult<PollStopStatsResponse>;
    const chatInfo: ChatType['chatInfo'] = yield select((state: RootState) => state.chats.chatInfo);
    const chatPoll = chatInfo.find((message) => message.poll?.i === pollId)?.poll;
    if (!chatPoll) {
      yield put(stopPollSuccess({ poll: { a: false, i: pollId, oo: [] } }));
      return;
    }

    const incomingPoll = { ...mergePollVoteStats(chatPoll, data, chatPoll.aa ?? []), a: false };
    yield put(stopPollSuccess({ poll: incomingPoll }));
  } catch (error) {
    yield put(stopPollFailed());
  }
}

function* getPollHandler({ payload }: ActionType<{ pollId: string }>) {
  const { pollId } = payload;
  try {
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}${pollApiPaths.get(pollId)}`, {
      headers: { Accept: 'application/json', access: '*/*' },
      method: 'GET',
    })) as SafeFetchResult<PollResultsDetails>;
    yield put(getPollSuccess(data));
  } catch (error) {
    yield put(getPollFailed(errorMessage(error)));
  }
}

function* getPollAnswerUsersHandler({ payload }: ActionType<GetPollAnswerUsersPayload>) {
  const { pollId, optionId, limit = POLL_ANSWER_USERS_PAGE_SIZE, page = 1 } = payload;
  try {
    const query = `?limit=${limit}&page=${page}`;
    const { data } = (yield call(safeFetch, `${BASE_CHAT_URL}${pollApiPaths.answerUsers(pollId)}/${optionId}${query}`, {
      headers: { Accept: 'application/json', access: '*/*' },
      method: 'GET',
    })) as SafeFetchResult<PollResultsVoter[]>;
    yield put(getPollAnswerUsersSuccess({ data, limit, optionId, page, pollId }));
  } catch (error) {
    yield put(getPollAnswerUsersFailed(errorMessage(error)));
  }
}

export default function* createChatWatcher(): Generator {
  yield takeEvery(GET_USERS_LIST, getUsersList);
  yield takeLatest(OPEN_DIALOG_WITH_USER, openDialogWithUser);
  yield takeEvery(PIN_CHAT, pinChat);
  yield takeEvery(UNPIN_CHAT, unpinChat);
  yield takeEvery(ENABLE_NOTIFICATIONS, enableNotifications);
  yield takeEvery(DISABLE_NOTIFICATIONS, disableNotifications);
  yield takeLatest(FETCH_CHATS, fetchChats);
  yield takeEvery(GET_CHAT_INFO, getChatInfo);
  yield takeEvery(CREATE_GROUP, createGroup);
  yield takeEvery(DELETE_CHAT, deleteChat);
  yield takeEvery(UPDATE_MESSAGE, updateMessage);
  yield takeEvery(DELETE_MESSAGES, deleteMessages);
  yield takeEvery(GET_DIALOG_INFO, getDialogInfo);
  yield takeEvery(GET_USERS_STATUS, getUsersStatus);
  yield takeEvery(READ_MESSAGES, readMessagesHandler);
  yield takeEvery(INVITE_USER, inviteUser);
  yield takeEvery(UPLOAD_ATTACHED_FILE, uploadAttachedFile);
  yield takeEvery(UPLOAD_GROUP_AVATAR, uploadGroupAvatar);
  yield takeEvery(UPDATE_GROUP, updateGroupHandler);
  yield takeEvery(LEAVE_GROUP, leaveGroup);
  yield takeLatest(SEARCH_IN_DIALOG, searchInDialog);
  yield takeEvery(NAVIGATE_TO_CHAT_MESSAGE_REQUEST, navigateToChatMessageRequestHandler);
  yield takeEvery(DELETE_GROUP_CHAT, deleteGroupChat);
  yield fork(getUpdatedChatInfoWatcher);
  yield takeEvery(FETCH_LAST_CHAT, fetchLastChat);
  yield takeEvery(GET_CHAT_FOLDERS, getChatFolders);
  yield takeEvery(REORDER_FOLDERS, reorderFolders);
  yield takeEvery(CREATE_CHAT_FOLDER, createChatFolder);
  yield takeEvery(EDIT_CHAT_FOLDER, editChatFolder);
  yield takeEvery(DELETE_CHAT_FOLDER, deleteChatFolder);
  yield takeEvery(GET_FOLDER_DIALOGS, getFolderDialogsHandler);
  yield takeEvery(MAKE_USER_ADMIN, makeAdmin);
  yield takeEvery(REMOVE_ADMIN, removeAdmin);
  yield takeEvery(BLOCK_DIALOG, blockDialog);
  yield takeEvery(UNBLOCK_DIALOG, unblockDialog);
  yield takeEvery(SEARCH_DIALOGS, searchDialogsHandler);
  yield takeEvery(SEARCH_GLOBAL_CHAT, searchChatsHandler);
  yield takeEvery(CREATE_POLL, createPollHandler);
  yield takeEvery(SUBMIT_POLL_ANSWER, submitPollAnswerHandler);
  yield takeEvery(STOP_POLL, stopPollHandler);
  yield takeLatest(GET_POLL, getPollHandler);
  yield takeLatest(GET_POLL_ANSWER_USERS, getPollAnswerUsersHandler);
}
